import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, Clock, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const { t } = useTranslation();
  
  // Generate random math problem
  const generateMathProblem = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return { num1, num2, answer: num1 + num2 };
  };

  const [mathProblem, setMathProblem] = useState(generateMathProblem());
  const [mathAnswer, setMathAnswer] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    email: '',
    phone: '',
    message: ''
  });
  const [characterCount, setCharacterCount] = useState(0);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (formData.name.length < 6) {
      newErrors.name = 'Ad Soyad en az 6 karakter olmalıdır';
    }

    if (formData.subject.length < 5) {
      newErrors.subject = 'Konu en az 5 karakter olmalıdır';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi girin';
    }

    const phoneRegex = /^\(5\d{2}\) \d{3} \d{2} \d{2}$/;
    if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Telefon formatı: (5XX) XXX XX XX';
    }

    if (formData.message.length < 100) {
      newErrors.message = 'Mesaj en az 100 karakter olmalıdır';
    }

    if (formData.message.length > 300) {
      newErrors.message = 'Mesaj en fazla 300 karakter olabilir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    
    if (value.length > 10) value = value.slice(0, 10);
    
    if (value.length >= 3) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    }
    if (value.length >= 10) {
      value = `${value.slice(0, 9)} ${value.slice(9)}`;
    }
    if (value.length >= 13) {
      value = `${value.slice(0, 12)} ${value.slice(12)}`;
    }

    setFormData({ ...formData, phone: value });
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    if (value.length <= 300) {
      setFormData({ ...formData, message: value });
      setCharacterCount(value.length);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(t('contact.form.errorMessage'));
      return;
    }

    // Validate math answer
    if (parseInt(mathAnswer) !== mathProblem.answer) {
      toast.error('Matematik işleminin sonucu yanlış. Lütfen tekrar deneyin.');
      setMathProblem(generateMathProblem());
      setMathAnswer('');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      console.log('Contact form response:', data);

      if (response.ok) {
        // Show success message
        toast.success('✅ Mesajınız başarıyla gönderildi!', {
          duration: 5000,
          style: {
            background: '#10b981',
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: '500'
          }
        });
        
        // Reset form
        setFormData({
          name: '',
          subject: '',
          email: '',
          phone: '',
          message: ''
        });
        setCharacterCount(0);
        setErrors({});
        setMathProblem(generateMathProblem());
        setMathAnswer('');
      } else {
        toast.error(data.detail || 'Mesaj gönderilemedi. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Sunucu hatası. Lütfen tekrar deneyin.');
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      subject: '',
      email: '',
      phone: '',
      message: ''
    });
    setCharacterCount(0);
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-4 text-gray-900">
          {t('contact.title')}
        </h1>
        <p className="text-center text-xl text-gray-600 mb-12">
          {t('contact.subtitle')}
        </p>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                {t('contact.info.workingHours')}
              </h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start space-x-3">
                  <Clock size={20} className="text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <p>{t('contact.info.weekdays')}</p>
                    <p>{t('contact.info.weekends')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-900">İletişim Bilgileri</h3>
              <div className="space-y-4 text-gray-700">
                <div className="flex items-center space-x-3">
                  <Mail size={20} className="text-red-600 flex-shrink-0" />
                  <span>info@canecorsoturkey.org</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone size={20} className="text-red-600 flex-shrink-0" />
                  <span>www.canecorsoturkey.org</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin size={20} className="text-red-600 flex-shrink-0" />
                  <span>{t('contact.info.addressValue')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.name')} *
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('contact.form.namePlaceholder')}
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.subject')} *
                  </label>
                  <Input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder={t('contact.form.subjectPlaceholder')}
                    className={errors.subject ? 'border-red-500' : ''}
                  />
                  {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.email')} *
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t('contact.form.emailPlaceholder')}
                      className={errors.email ? 'border-red-500' : ''}
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.phone')} *
                    </label>
                    <Input
                      type="text"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder={t('contact.form.phonePlaceholder')}
                      className={errors.phone ? 'border-red-500' : ''}
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.message')} * (100-300 {t('contact.form.characterCount')})
                  </label>
                  <Textarea
                    value={formData.message}
                    onChange={handleMessageChange}
                    placeholder={t('contact.form.messagePlaceholder')}
                    rows={6}
                    className={errors.message ? 'border-red-500' : ''}
                  />
                  <div className="flex justify-between items-center mt-2">
                    {errors.message && <p className="text-red-500 text-sm">{errors.message}</p>}
                    <span className={`text-sm ml-auto ${characterCount < 100 ? 'text-red-500' : 'text-gray-600'}`}>
                      {characterCount} / 300
                    </span>
                  </div>
                </div>

                {/* Math Verification */}
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🔒 Güvenlik Doğrulaması
                  </label>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold text-gray-900">
                      {mathProblem.num1} + {mathProblem.num2} = ?
                    </span>
                    <Input
                      type="number"
                      value={mathAnswer}
                      onChange={(e) => setMathAnswer(e.target.value)}
                      placeholder="Sonuç"
                      className="w-24"
                      required
                    />
                  </div>
                </div>

                <div className="flex space-x-4">
                  <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700">
                    <Send size={18} className="mr-2" />
                    {t('contact.form.send')}
                  </Button>
                  <Button 
                    type="button" 
                    onClick={handleCancel}
                    variant="outline"
                    className="flex-1"
                  >
                    {t('contact.form.cancel')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
